import PermissionButton from '../../permissions/PermissionButton';
import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tabs,
  Tag,
  Drawer,
  Form,
  Select,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useListCorrectionStocks,
  useCreateCorrectionStock,
} from '../../hooks/avoirHooks';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../hooks/entrepotHooks';
import { getDefaultEntrepot } from '../../utils/defaultEntrepot';

const { TabPane } = Tabs;

const statusTag = (validated) => (
  validated ? <Tag color="green">Validée</Tag> : <Tag color="orange">Enregistrée</Tag>
);

const CorrectionStockList = ({ onSelectCorrection, onBack }) => {
  const [activeTab, setActiveTab] = useState('draft');
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { items: allItems, loading, error, refetch } = useListCorrectionStocks({ page: 1, pageSize: 1000 });
  const { handleCreate, loading: creating } = useCreateCorrectionStock();
  const { entrepots, loading: loadingEntrepots } = useFetchAllEntrepots();
  const { defaultCode } = useDefaultEntrepot();

  const [form] = Form.useForm();

  const getDateColumn = () => ({
    title: activeTab === 'validated' ? 'Validation Date' : 'Creation Date',
    key: 'date',
    render: (record) => {
      const date = record.VALIDER ? record.DATEVALID : record.DATE_COR;
      return dayjs(date).format('DD/MM/YYYY');
    },
    sorter: (a, b) => {
      const dateA = a.VALIDER ? a.DATEVALID : a.DATE_COR;
      const dateB = b.VALIDER ? b.DATEVALID : b.DATE_COR;
      return dayjs(dateA).unix() - dayjs(dateB).unix();
    },
  });

  const columns = useMemo(() => [
    {
      title: 'Reference',
      dataIndex: 'REF_COR',
      key: 'REF_COR',
      render: (value, record) => (
        <Space>
          <span>{value}</span>
          {statusTag(record.VALIDER)}
        </Space>
      ),
    },
    getDateColumn(),
    {
      title: 'Entrepôt',
      key: 'entrepot',
      render: (record) => (
        <span>
          {record.entrepot?.name ? `${record.entrepot.code_ent} - ${record.entrepot.name}` : record.CODE_ENT}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'TYPE',
      key: 'TYPE',
      width: 100,
      render: (value) => (
        <Tag color={value === 'SORTIE' ? 'red' : 'green'}>
          {value === 'SORTIE' ? 'Sortie' : 'Entrée'}
        </Tag>
      ),
    },
    {
      title: 'Articles',
      dataIndex: 'detailCount',
      key: 'detailCount',
      width: 100,
      render: (value) => value || 0,
    },
    {
      title: 'Qté nette',
      dataIndex: 'totalQty',
      key: 'totalQty',
      width: 110,
      render: (value) => {
        const qty = Number(value) || 0;
        return (
          <Tag color={qty >= 0 ? 'green' : 'red'}>
            {qty > 0 ? `+${qty}` : qty}
          </Tag>
        );
      },
    },
    {
      title: 'Remarks',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
  ], [activeTab]);

  const handleStatusChange = (key) => {
    setActiveTab(key);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleCreateSubmit = async (values) => {
    try {
      const defaultEntrepot = await getDefaultEntrepot();
      const payload = {
        CODE_ENT: values.CODE_ENT || defaultEntrepot,
        TYPE: values.TYPE,
        REMARQUE: values.REMARQUE || undefined,
        details: [],
      };
      const created = await handleCreate(payload);
      message.success('Correction de stock créée');
      setCreateDrawerOpen(false);
      form.resetFields();
      await refetch();
      if (created?.REF_COR && onSelectCorrection) {
        onSelectCorrection(created.REF_COR);
      }
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la création de la correction de stock');
    }
  };

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
    marginTop: "-10px",
  };

  const tableCardStyle = {
    marginTop: "20px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  const getFilteredCorrections = () => {
    if (!allItems) return [];
    let filtered = activeTab === 'validated'
      ? allItems.filter(d => d.VALIDER)
      : allItems.filter(d => !d.VALIDER);

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(d =>
        d.REF_COR?.toLowerCase().includes(searchLower) ||
        d.REMARQUE?.toLowerCase().includes(searchLower) ||
        d.entrepot?.name?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Card style={tableCardStyle}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Retour</Button>
          <h2 style={titleStyle}>Correction Stock List</h2>
        </div>
        <Space>
          <Input.Search
            placeholder="Rechercher"
            allowClear
            onSearch={handleSearch}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);
              if (!value) handleSearch('');
            }}
            value={searchText}
            style={{ width: 220 }}
          />
          <PermissionButton permissionModule="stockCorrections" permissionAction="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ CODE_ENT: defaultCode || undefined, TYPE: 'ENTREE' });
              setCreateDrawerOpen(true);
            }}
          >
            Create Correction
          </PermissionButton>
        </Space>
      </Space>
      <Tabs defaultActiveKey="draft" onChange={handleStatusChange}>
        <TabPane tab="Not Validated" key="draft" />
        <TabPane tab="Validated" key="validated" />
      </Tabs>

      <Table
        columns={columns}
        dataSource={getFilteredCorrections()}
        rowKey="REF_COR"
        pagination={{ pageSize: 10 }}
        onRow={(record) => ({
          onClick: () => onSelectCorrection && onSelectCorrection(record.REF_COR),
          style: { cursor: 'pointer' }
        })}
      />

      <Drawer
        title="Nouvelle correction de stock"
        width={480}
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleCreateSubmit}
          initialValues={{
            CODE_ENT: defaultCode || undefined,
            TYPE: 'ENTREE',
          }}
        >
          <Form.Item
            name="CODE_ENT"
            label="Entrepôt"
            rules={[{ required: true, message: 'Sélectionnez un entrepôt' }]}
          >
            <Select
              showSearch
              placeholder="Choisir un entrepôt"
              loading={loadingEntrepots}
              options={(entrepots || []).map((ent) => ({
                value: ent.code_ent,
                label: `${ent.code_ent} - ${ent.name}`,
              }))}
              filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item
            name="TYPE"
            label="Type"
            rules={[{ required: true, message: 'Sélectionnez Entrée ou Sortie' }]}
          >
            <Select
              options={[
                { value: 'ENTREE', label: 'Entrée' },
                { value: 'SORTIE', label: 'Sortie' },
              ]}
            />
          </Form.Item>

          <Form.Item name="REMARQUE" label="Remarque">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <PermissionButton   onClick={() => setCreateDrawerOpen(false)}>Annuler</PermissionButton>
            <PermissionButton permissionModule="stockCorrections" permissionAction="create" type="primary" htmlType="submit" loading={creating}>
              Créer
            </PermissionButton>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default CorrectionStockList;
